import React from 'react'
import styled, {css} from 'styled-components'
import {Duration} from 'lib/date-time'
import {useAttendeeVariables} from 'Event'
import {Font} from 'lib/FontSelect'
import {BOLD, CAPS, ITALIC} from 'lib/ui/typography/FontStyleInput'
import {CountDownTimerSettings} from 'Event/Dashboard/components/CountDownTimer'

type CountDownTimerUnitsProps = CountDownTimerSettings & {
  id: string
  timeLeft: Duration
}

export default function CountDownTimerUnits(props: CountDownTimerUnitsProps) {
  const {id, timeLeft, labels} = props
  const v = useAttendeeVariables()
  const entries = Object.entries(timeLeft)

  return (
    <TimerUnits>
      {entries.map(([key, value], index) => {
        const isLast = index === entries.length - 1
        return (
          <React.Fragment key={`${id}-${key}`}>
            <CountDownUnit {...props}>
              <NumberBlock {...props}>{value}</NumberBlock>
              <LabelBlock {...props}>
                {v(labels[key as keyof Duration])}
              </LabelBlock>
            </CountDownUnit>

            {!isLast && (
              <CountDownSeparator {...props}>
                {key === 'days' ? ' ' : v(labels.separator)}
              </CountDownSeparator>
            )}
          </React.Fragment>
        )
      })}
    </TimerUnits>
  )
}

const convertFont = (font: Font | null) => {
  if (!font) return
  return css`
    font-weight: ${font.styles?.includes(BOLD) ? 'bold' : 'normal'};
    font-family: ${font.family};
    font-style: ${font.styles?.includes(ITALIC) ? 'italic' : 'normal'};
    text-transform: ${font.styles?.includes(CAPS) ? 'uppercase' : 'none'};
  `
}

const TimerUnits = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  gap: clamp(0.25em, 1.5cqw, 1em);
  box-sizing: border-box;
  flex-wrap: nowrap;

  max-width: 100%;
`

const CountDownUnit = styled.div<{
  unitBackground: CountDownTimerSettings['unitBackground']
  numberStyles: CountDownTimerSettings['numberStyles']
  labelStyles: CountDownTimerSettings['labelStyles']
}>`
  position: relative;
  border-radius: ${(props) => props.unitBackground.radius}%;
  padding: ${(props) => props.unitBackground.padding}px 0;
  width: calc(
    ${(props) => props.unitBackground.padding * 2}px +
      ${(props) =>
        Math.max(
          props.numberStyles.fontSize || 16,
          props.labelStyles.fontSize || 16,
        )}px * 3.5
  );

  max-width: 22%;
  height: max-content;
  flex: 0 1 auto;
  min-width: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  isolation: isolate;

  aspect-ratio: 1 / 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: ${(props) => props.unitBackground.color};
    background-position: center;
    background-size: cover;
    opacity: ${(props) => (props.unitBackground.opacity ?? 100) / 100};
  }
`

const NumberBlock = styled.div<{
  numberStyles: CountDownTimerSettings['numberStyles']
}>`
  color: ${(props) => props.numberStyles.color || '#000000'};
  ${(props) => convertFont(props.numberStyles.font)}

  text-align: center;
  white-space: nowrap;
  line-height: 1;
  max-width: 100%;

  font-size: clamp(
    12px,
    13cqw,
    ${(props) => props.numberStyles.fontSize || 16}px
  );

  @media (max-width: ${(props) => props.theme?.breakpoints?.md}) {
    font-size: clamp(
      12px,
      10cqw,
      ${(props) => props.numberStyles.fontSize || 16}px
    );
  }
`

const LabelBlock = styled.div<{
  labelStyles: CountDownTimerSettings['labelStyles']
}>`
  color: ${(props) => props.labelStyles.color || '#000000'};
  ${(props) => convertFont(props.labelStyles.font)}

  text-align: center;
  white-space: nowrap;
  line-height: 1;
  margin-top: 0.25em;
  max-width: 100%;

  font-size: clamp(
    8px,
    13cqw,
    ${(props) => props.labelStyles.fontSize || 16}px
  );

  @media (max-width: ${(props) => props.theme?.breakpoints?.md}) {
    font-size: clamp(
      8px,
      10cqw,
      ${(props) => props.labelStyles.fontSize || 16}px
    );
  }
`

const CountDownSeparator = styled.span<{
  separatorStyles: CountDownTimerSettings['separatorStyles']
  numberStyles: CountDownTimerSettings['numberStyles']
}>`
  color: ${(props) => props.separatorStyles.color || '#000000'};
  ${(props) =>
    props.numberStyles.font
      ? `font-family: ${props.numberStyles.font.family};`
      : ''}
  flex-shrink: 0;
  font-size: clamp(
    12px,
    4cqw,
    ${(props) => props.numberStyles.fontSize || 16}px
  );
`
